import { API, RequestMethodOpts } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import fileDownload from "js-file-download";

/**
 * Creates a file to download directly in the Browser
 *
 * @param data
 * @param fileName
 * @param mimeType
 */
export async function download(
  data: string | ArrayBuffer | File | Blob,
  fileName: string,
  mimeType = "application/octet-stream"
) {
  // iOS detection
  if (
    (/iPad|iPhone|iPod/.test(navigator.platform) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    !(window as any).MSStream
  ) {
    let objectUrl: string;
    if (data instanceof Blob) {
      objectUrl = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result as string);
        reader.onerror = () => rej();
        reader.readAsDataURL(data);
      });
    } else {
      const blob = new Blob([data], { type: mimeType });
      objectUrl = window.URL.createObjectURL(blob);
    }
    const link = document.createElement("a");
    link.href = objectUrl;
    link.target = "_self";
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } else {
    fileDownload(data, fileName);
  }

  // let blob = new Blob([data], { type: mimeType });
  // if (window.navigator.msSaveBlob) { // // IE hack; see http://msdn.microsoft.com/en-us/library/ie/hh779016.aspx
  //   window.navigator.msSaveBlob(blob, fileName);
  // } else if (window.navigator.userAgent.indexOf('Firefox') > -1) {
  //   const file = new File([blob], fileName, { type: 'application/force-download' });
  //   window.open(window.URL.createObjectURL(file));
  // } else {
  //   let link = document.createElement('a');
  //   link.download = fileName;
  //   link.href = window
  //     .URL
  //     .createObjectURL(blob);
  //   link.click();
  //   link.remove();
  // }
}

export async function downloadProjectFile(
  fileData: API_FileRequest | API_FileRequest[],
  formBodyOpts?: API_DownloadWizardProperties,
  requestOpts?: RequestMethodOpts
) {
  try {
    const isArray = Array.isArray(fileData);
    const url = API_URL.FILES.getDownloadWizard2Response(projectsDataStore.projectId);
    const formData: API_DownloadWizardProperties = {
      files: (isArray ? fileData : [fileData]) as API_FileRequest[],
      withDirectories: false,
      withWatermark: false,
      withQR: false,
      pdfWherePossible: false,
      ...formBodyOpts,
    };
    const file: File = await API.getBlobByPost(url, formData, null, requestOpts);
    download(file, file.name, file.type);
  } catch (error) {
    throw error;
  }
  // downloadByURL(API_URL.FILES.getFileById(projectsDataStore.projectId, id));
}

export async function downloadByURL(url: string, fileName?: string) {
  const file: File = await API.getBlob(url);

  if (fileName) {
    download(file, fileName, file.type);
  } else {
    download(file, file.name, file.type);
  }
}

const EXCEL_MIME_TYPES: Record<string, string> = {
  xls: "text/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};

export async function downloadExcel(name: string, url: string, params?: Object) {
  try {
    const excel = await API.getBlob(url, params);
    const format = (await isXlsxWorkbook(excel)) ? "xlsx" : "xls";
    download(excel, `${name}.${format}`, EXCEL_MIME_TYPES[format]);
  } catch (err) {
    throw err;
  }
}

async function isXlsxWorkbook(excel: File): Promise<boolean> {
  const signature = new Uint8Array(await excel.slice(0, 2).arrayBuffer());
  return signature[0] === 0x50 && signature[1] === 0x4b;
}

/** Payload of LongRunner.finish(StreamedFile) — content is Base64. */
export interface StreamedFilePayload {
  filename: string;
  mimeType?: string;
  content: string;
}

export function fileFromStreamedPayload(payload: StreamedFilePayload): File {
  const binary = atob(payload.content);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  const mimeType = payload.mimeType || "application/octet-stream";
  return new File([bytes], payload.filename, { type: mimeType });
}

export function downloadDataUri(uri: string, filename: string) {
  const link = document.createElement("a");
  link.href = uri;
  link.target = "_self";
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
